import { useState, useEffect, useCallback } from 'react';
import { OpenAPI } from '../../../api/core/OpenAPI';

interface UseValueChainMetaInfoResult {
  info: string | null;
  shortcomings: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch value chain meta info (info and shortcomings) for a given value chain name.
 * @param valueChainName - The name of the value chain to fetch meta info for
 */
export function useValueChainMetaInfo(valueChainName: string | null | undefined): UseValueChainMetaInfoResult {
  const [info, setInfo] = useState<string | null>(null);
  const [shortcomings, setShortcomings] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMetaInfo = useCallback(async () => {
    if (!valueChainName) {
      setInfo(null);
      setShortcomings(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = OpenAPI.BASE || 'http://localhost:8000';
      const response = await fetch(
        `${baseUrl}/api/v1/companies/value-chain-meta/${encodeURIComponent(valueChainName)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch value chain meta info: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        setInfo(result.data.info || null);
        setShortcomings(result.data.shortcomings || null);
      } else {
        setInfo(null);
        setShortcomings(null);
      }
    } catch (err) {
      console.error('Error fetching value chain meta info:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setInfo(null);
      setShortcomings(null);
    } finally {
      setLoading(false);
    }
  }, [valueChainName]);

  useEffect(() => {
    fetchMetaInfo();
  }, [fetchMetaInfo]);

  return {
    info,
    shortcomings,
    loading,
    error,
    refetch: fetchMetaInfo,
  };
}

export default useValueChainMetaInfo;

